import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import Dropzone from 'react-dropzone';
import classNames from 'classnames';
import { useFeatureFlagsCtx } from 'contexts/FeatureFlags';
import { getIsVideoPodcaster } from 'modules/FeatureFlags';
import { DropdownMenuContainer } from 'components/DropdownMenu/DropdownMenuContainer';
import { useQuickUploadFile } from 'client/contexts/QuickUploadFile';
import { useCreateEpisode } from 'hooks/episodes/useCreateEpisode';
import {
  ACCEPTED_FILE_TYPES_AUDIO_USERS,
  ACCEPTED_FILE_TYPES_VIDEO_USERS,
} from 'screens/EpisodeOptionsScreen/components/EpisodeContent/constants';
import {
  clickedCreateEpisodeButton,
  clickedQuickUploadButton,
} from 'components/PublicWebsiteNav/events';
import { useT } from 'client/i18n/hooks/useT';
import styles from '../../../../shared/Button/styles.sass';

const CreateNewEpisodeButtonUnwrapped = (props: RouteComponentProps) => {
  const t = useT();
  const cx = classNames(styles.button, styles.onDark);
  const { history } = props;
  const { setQuickUploadFile } = useQuickUploadFile();
  const { createNewEpisode } = useCreateEpisode();

  const {
    state: { featureFlags },
  } = useFeatureFlagsCtx();
  const isVideoPodcaster = getIsVideoPodcaster(featureFlags);

  const handleOnDrop = (acceptedFiles: File[]) => {
    setQuickUploadFile(acceptedFiles[0]);
    // create new shell episode and redirect
    // to Episode Options page for new episode
    createNewEpisode(
      {},
      {
        onSuccess: data => {
          history.push(`/dashboard/episode/${data.webEpisodeId}/metadata/edit`);
        },
      }
    );
  };

  return (
    <Dropzone
      onDrop={handleOnDrop}
      accept={
        isVideoPodcaster
          ? ACCEPTED_FILE_TYPES_VIDEO_USERS
          : ACCEPTED_FILE_TYPES_AUDIO_USERS
      }
      noClick
      noKeyboard
      maxFiles={1}
    >
      {({ getRootProps, getInputProps, open }) => {
        const menuItems = [
          {
            type: 'link',
            label: t(
              'PublicWebsiteNavFooter.components.CreateNewEpisodeButton.CreateAnEpisode',
              'Create an episode',
              'Link to "Create an episode" web page'
            ),
            description: t(
              'PublicWebsiteNavFooter.components.CreateNewEpisodeButton.CreateAnEpisode.Description',
              'Record, edit, and enhance audio',
              'Link to "Create an episode" web page description'
            ),
            icon: {
              type: 'MicIcon',
              iconColor: '#000000',
              backgroundColor: 'transparent',
              width: 20,
              padding: 0,
            },
            to: '/dashboard/episode/new',
            onClick: () => {
              clickedCreateEpisodeButton();
            },
          },
          {
            type: 'separator',
          },
          {
            type: 'item',
            label: t(
              'PublicWebsiteNavFooter.components.CreateNewEpisodeButton.QuickUpload',
              'Quick upload',
              'Link to "Quick upload" web page'
            ),
            labelTag: isVideoPodcaster
              ? t(
                  'PublicWebsiteNavFooter.components.CreateNewEpisodeButton.QuickUpload.LabelTag',
                  'SUPPORTS VIDEO',
                  'Link to "Quick upload" web page clarifying tag'
                )
              : null,
            description: isVideoPodcaster
              ? t(
                  'PublicWebsiteNavFooter.components.CreateNewEpisodeButton.QuickUpload.CreateFileDescription',
                  'Create an episode from an audio or video file',
                  'Link to "Quick upload" web page description'
                )
              : t(
                  'PublicWebsiteNavFooter.components.CreateNewEpisodeButton.QuickUpload.ExistingFileDescription',
                  'Publish an episode from an existing file',
                  'Link to "Quick upload" web page description'
                ),
            icon: {
              type: 'UploadIcon',
              iconColor: '#c9cbcd',
              backgroundColor: 'transparent',
              width: 20,
              padding: 0,
            },
            onClick: () => {
              clickedQuickUploadButton();
              open();
            },
          },
        ];

        return (
          <div {...getRootProps()} style={{ textAlign: 'left' }}>
            <input {...getInputProps()} />
            <DropdownMenuContainer
              buttonStyles={cx}
              renderPressableContent={t(
                'PublicWebsiteNavFooter.NewEpisodeButtonText',
                'New Episode',
                'Button to open "New Episode" nav dropdown'
              )}
              shouldShowDistributionItem={false}
              menuItems={menuItems}
              flyoutBorderRadius={16}
              flyoutMarginTop={4}
              flyoutPaddingTopBottom={0}
              flyoutWidth={278}
              alignFlyout="bottom"
            />
          </div>
        );
      }}
    </Dropzone>
  );
};

const CreateNewEpisodeButton = withRouter(CreateNewEpisodeButtonUnwrapped);
export { CreateNewEpisodeButton };
